import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api, API, errMsg } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function Login() {
  const { login, verifyMfa } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [loginErr, setLoginErr] = useState('');
  const [ssoId, setSsoId] = useState('');

  useEffect(() => {
    const err = new URLSearchParams(window.location.search).get('sso_error');
    if (err) toast.error(`SSO sign-in failed: ${err}`);
  }, []);

  const doSso = async () => {
    setBusy(true);
    try {
      const r = await api.get('/auth/sso/resolve', { params: { email_or_domain: ssoId } });
      if (r.data.available) window.location.href = `${API}/auth/sso/${r.data.tenant_id}/login`;
      else toast.error('SSO is not enabled for this domain. Ask your admin to enable it in IAM & IGA.');
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const doLogin = async (em, pw) => {
    setBusy(true);
    setLoginErr('');
    try {
      const r = await login(em, pw);
      if (r.mfaToken) { setMfaToken(r.mfaToken); toast.info('Enter your MFA code'); }
      else nav('/');
    } catch (e) { setLoginErr(errMsg(e)); toast.error(errMsg(e)); }
    setBusy(false);
  };

  const doMfa = async () => {
    setBusy(true);
    try { await verifyMfa(mfaToken, code); nav('/'); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 noise-bg">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/35385546/pexels-photo-35385546.jpeg" alt="Campus" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <p className="font-mono-label text-xs uppercase text-slate-300 mb-3">Identity-first · AI-native · Multi-tenant</p>
          <h2 className="font-display text-4xl font-extrabold tracking-tight max-w-md">The Education Operating System for schools & universities</h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <img src="/brand/onrid-banner-transparent.png" alt="ONRID" className="h-14 w-auto object-contain" data-testid="brand-banner" />
          </div>
          <p className="font-mono-label text-[10px] uppercase tracking-widest text-orange-600 mb-1">Know everyone. Trust the record.</p>
          {!mfaToken ? (
            <>
              <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Sign in</h1>
              <p className="text-sm text-slate-500 mb-6">Every persona gets its own shell. Sign in with your email and password.</p>
              <form onSubmit={(e) => { e.preventDefault(); doLogin(email, password); }} className="space-y-3">
                <Input data-testid="login-email-input" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <Input data-testid="login-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                <Button data-testid="login-submit-button" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800" type="submit">{busy ? 'Signing in...' : 'Sign in'}</Button>
              </form>
              {loginErr && <p role="alert" data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mt-3">{loginErr}</p>}
              <div className="flex items-center justify-between mt-3 text-xs">
                <a href="/forgot-password" data-testid="forgot-password-link" className="text-slate-500 hover:text-slate-900 underline">Forgot password?</a>
                <a href="/signup" data-testid="signup-link" className="text-orange-600 hover:text-orange-700 font-semibold underline">Create your school account</a>
              </div>
              <div className="mt-5 pt-5 border-t border-slate-200">
                <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-2">Enterprise single sign-on (Okta / Azure AD)</p>
                <div className="flex gap-2">
                  <Input data-testid="login-sso-input" placeholder="Work email or domain" value={ssoId} onChange={(e) => setSsoId(e.target.value)} />
                  <Button data-testid="login-sso-button" variant="outline" disabled={!ssoId || busy} onClick={doSso} className="shrink-0">Continue with SSO</Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">MFA verification</h1>
              <p className="text-sm text-slate-500 mb-6">Enter the 6-digit code from your authenticator app.</p>
              <Input data-testid="mfa-code-input" placeholder="123456" value={code} onChange={(e) => setCode(e.target.value)} className="mb-3" />
              <Button data-testid="mfa-verify-button" disabled={busy} onClick={doMfa} className="w-full bg-slate-900 hover:bg-slate-800">Verify</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
